function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function requiredText(value, label) {
  const normalized = typeof value === 'string' ? value.trim() : '';
  if (!normalized) throw new TypeError(`${label} is required.`);
  return normalized;
}

function instant(value, label) {
  const normalized = requiredText(value, label);
  const parsed = new Date(normalized);
  if (!Number.isFinite(parsed.getTime())) throw new TypeError(`${label} is invalid.`);
  return parsed.toISOString();
}

function driverOptions(drivers, selected = '') {
  return [
    '<option value="">Choose Driver</option>',
    ...drivers.map((driver) => `<option value="${escapeHtml(driver.id)}"${driver.id === selected ? ' selected' : ''}>${escapeHtml(driver.fullName)}</option>`),
  ].join('');
}

function field(label, name, { type = 'text', value = '', required = true } = {}) {
  return `<label><span>${escapeHtml(label)}</span><input name="${escapeHtml(name)}" type="${escapeHtml(type)}" value="${escapeHtml(value)}"${required ? ' required' : ''}></label>`;
}

function driverField(label, name, drivers, selected = '', { required = true } = {}) {
  return `<label><span>${escapeHtml(label)}</span><select name="${escapeHtml(name)}"${required ? ' required' : ''}>${driverOptions(drivers, selected)}</select></label>`;
}

function legMarkup({ label, key, drivers, values, fromLabel, toLabel }) {
  return `
    <div class="dispatch-plan-leg" role="group" aria-label="${escapeHtml(label)}">
      <span class="dispatch-plan-leg__label">${escapeHtml(label)}</span>
      <div class="dispatch-plan-grid">
        ${driverField('Driver', `${key}_driver_id`, drivers, values[`${key}_driver_id`] || '')}
        ${field('Date & Time', `${key}_at`, { type: 'datetime-local', value: values[`${key}_at`] || '' })}
        ${field(fromLabel, `${key}_from_name`, { value: values[`${key}_from_name`] || '' })}
        ${field(`${fromLabel} Address`, `${key}_from_address`, { value: values[`${key}_from_address`] || '' })}
        ${field(toLabel, `${key}_to_name`, { value: values[`${key}_to_name`] || '' })}
        ${field(`${toLabel} Address`, `${key}_to_address`, { value: values[`${key}_to_address`] || '' })}
        ${field('Note', `${key}_notes`, { value: values[`${key}_notes`] || '', required: false })}
      </div>
    </div>`;
}

export function dispatchPlanWorkspaceMarkup({ action, drivers = [], values = {} } = {}) {
  if (!action?.id || !action?.container_id || !action?.container_no) {
    throw new TypeError('Dispatch planning requires an Action and Container identity.');
  }
  if (!Array.isArray(drivers) || drivers.some((driver) => !driver?.id || !driver?.fullName)) {
    throw new TypeError('Dispatch planning requires valid Driver choices.');
  }
  return `
    <section class="workflow-page workflow-page--workspace-action dispatch-plan-workspace">
      <div class="workflow-context-number" data-workspace-container-number>${escapeHtml(action.container_no)}</div>
      <form class="workflow-form workflow-form--workspace dispatch-plan-form" data-dispatch-plan-form novalidate>
        <div class="dispatch-plan-grid dispatch-plan-grid--identity">
          ${field('Chassis #', 'chassis_no', { value: values.chassis_no || '' })}
          ${field('Empty Container #', 'empty_container_no', { value: values.empty_container_no || '' })}
          ${field('Pickup Appointment', 'pickup_appointment_at', { type: 'datetime-local', value: values.pickup_appointment_at || '' })}
        </div>
        ${legMarkup({ label: 'Transfer to Yard', key: 'transfer', drivers, values, fromLabel: 'From', toLabel: 'To' })}
        ${legMarkup({ label: 'Loaded Delivery', key: 'loaded_pickup', drivers, values, fromLabel: 'Pickup From', toLabel: 'Deliver To' })}
        ${legMarkup({ label: 'Empty Pickup', key: 'empty_pickup', drivers, values, fromLabel: 'Pickup From', toLabel: 'Stage At' })}
        ${legMarkup({ label: 'Empty Return', key: 'empty_return', drivers, values, fromLabel: 'Return From', toLabel: 'Terminal' })}
        <p class="workflow-form-error" data-dispatch-plan-error role="alert" aria-live="assertive"></p>
        <button type="submit" data-workspace-done-target hidden>Save Dispatch Plan</button>
      </form>
    </section>`;
}

export function buildDispatchPlanWorkspaceRequest(values) {
  const point = (name, address, type, prefix) => ({
    name: requiredText(name, `${prefix} name`),
    address: requiredText(address, `${prefix} address`),
    type,
  });
  const leg = ({ kind, driverId, at, fromName, fromAddress, fromType, toName, toAddress, toType, notes }) => ({
    kind,
    driverId: requiredText(driverId, `${kind} Driver`),
    scheduledStartAt: instant(at, `${kind} time`),
    origin: point(fromName, fromAddress, fromType, `${kind} origin`),
    destination: point(toName, toAddress, toType, `${kind} destination`),
    notes: typeof notes === 'string' ? notes.trim() : '',
  });
  const expectedRevision = Number(values.expectedRevision);
  if (!Number.isSafeInteger(expectedRevision) || expectedRevision < 0) {
    throw new TypeError('Dispatch Action revision is invalid.');
  }
  return {
    actionId: requiredText(values.actionId, 'Action'),
    containerId: requiredText(values.containerId, 'Container'),
    requestId: requiredText(values.requestId, 'Request'),
    expectedRevision,
    chassisNo: requiredText(values.chassisNo, 'Chassis #'),
    emptyContainerNo: requiredText(values.emptyContainerNo, 'Empty Container #').toUpperCase(),
    pickupAppointmentAt: instant(values.pickupAppointmentAt, 'Pickup appointment'),
    legs: [
      leg({
        kind: 'transfer', driverId: values.transferDriverId, at: values.transferAt,
        fromName: values.transferFromName, fromAddress: values.transferFromAddress, fromType: 'terminal',
        toName: values.transferToName, toAddress: values.transferToAddress, toType: 'yard',
        notes: values.transferNotes,
      }),
      leg({
        kind: 'loaded_pickup', driverId: values.loadedPickupDriverId, at: values.loadedPickupAt,
        fromName: values.loadedPickupFromName, fromAddress: values.loadedPickupFromAddress, fromType: 'yard',
        toName: values.loadedPickupToName, toAddress: values.loadedPickupToAddress, toType: 'customer',
        notes: values.loadedPickupNotes,
      }),
      leg({
        kind: 'empty_pickup', driverId: values.emptyPickupDriverId, at: values.emptyPickupAt,
        fromName: values.emptyPickupFromName, fromAddress: values.emptyPickupFromAddress, fromType: 'customer',
        toName: values.emptyPickupToName, toAddress: values.emptyPickupToAddress, toType: 'yard',
        notes: values.emptyPickupNotes,
      }),
      leg({
        kind: 'empty_return', driverId: values.emptyReturnDriverId, at: values.emptyReturnAt,
        fromName: values.emptyReturnFromName, fromAddress: values.emptyReturnFromAddress, fromType: 'yard',
        toName: values.emptyReturnToName, toAddress: values.emptyReturnToAddress, toType: 'terminal',
        notes: values.emptyReturnNotes,
      }),
    ],
  };
}

export function emptyReturnPlanWorkspaceMarkup({ action, drivers = [], values = {} } = {}) {
  if (!action?.id || !action?.container_id || !action?.container_no) {
    throw new TypeError('Empty Return planning requires an Action and Container identity.');
  }
  return `
    <section class="workflow-page workflow-page--workspace-action dispatch-plan-workspace">
      <div class="workflow-context-number" data-workspace-container-number>${escapeHtml(action.container_no)}</div>
      <form class="workflow-form workflow-form--workspace dispatch-plan-form" data-empty-return-plan-form novalidate>
        <div class="dispatch-plan-leg" role="group" aria-label="Empty Return">
          <span class="dispatch-plan-leg__label">Empty Return</span>
          <div class="dispatch-plan-grid">
            ${driverField('Driver', 'empty_return_driver_id', drivers, values.empty_return_driver_id || '')}
            ${field('Return Appt Date', 'actual_empty_return_date', { type: 'date', value: values.actual_empty_return_date || '' })}
            ${field('Return Appt Time (optional)', 'actual_empty_return_time', { type: 'time', value: values.actual_empty_return_time || '', required: false })}
            ${field('Driver Schedule', 'empty_return_at', { type: 'datetime-local', value: values.empty_return_at || '' })}
            ${field('Return From', 'empty_return_from_name', { value: values.empty_return_from_name || '' })}
            ${field('Return From Address', 'empty_return_from_address', { value: values.empty_return_from_address || '' })}
            ${field('Terminal', 'empty_return_to_name', { value: values.empty_return_to_name || '' })}
            ${field('Terminal Address', 'empty_return_to_address', { value: values.empty_return_to_address || '' })}
            ${field('Note', 'empty_return_notes', { value: values.empty_return_notes || '', required: false })}
          </div>
        </div>
        <button type="button" class="secondary" data-save-actual-empty-return>Save Return Appt</button>
        <p class="workflow-form-error" data-empty-return-plan-error role="alert" aria-live="assertive"></p>
        <button type="submit" data-workspace-done-target hidden>Save Empty Return Plan</button>
      </form>
    </section>`;
}
